import React from 'react';
import { Box, Grid, Paper, Typography, useTheme } from '@mui/material';
import { CustomTheme } from '@mui/material';

const ColorBox = ({ color, name }: { color: string; name: string }) => (
  <Paper elevation={1} sx={{ p: 1, height: '100%' }}>
    <Box
      sx={{
        width: '100%',
        height: 60,
        backgroundColor: color,
        mb: 0.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
    <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: '0.75rem' }}>
      {name}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
      {color}
    </Typography>
  </Paper>
);

const ColorSection = ({ title, colors }: { title: string; colors: Record<string, string> }) => {
  // Filter out opacity values that aren't actual colors
  const filteredColors = Object.entries(colors).filter(([name, value]) => {
    // Skip opacity values
    if (name.toLowerCase().includes('opacity')) {
      return false;
    }
    // Also check if the value is a number (opacity values are typically numbers)
    if (typeof value === 'number') {
      return false;
    }
    return true;
  });

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>
        {title}
      </Typography>
      <Grid container spacing={1}>
        {filteredColors.map(([name, color]) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={name}>
            <ColorBox color={color} name={name} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const ColorsPage = () => {
  const theme = useTheme() as CustomTheme;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontSize: '1.25rem' }}>
        Color Palette
      </Typography>

      <ColorSection title="Primary" colors={theme.palette.primary as unknown as Record<string, string>} />
      <ColorSection title="Secondary" colors={theme.palette.secondary as unknown as Record<string, string>} />
      <ColorSection title="Error" colors={theme.palette.error as unknown as Record<string, string>} />
      <ColorSection title="Warning" colors={theme.palette.warning as unknown as Record<string, string>} />
      <ColorSection title="Info" colors={theme.palette.info as unknown as Record<string, string>} />
      <ColorSection title="Success" colors={theme.palette.success as unknown as Record<string, string>} />
      <ColorSection title="Text" colors={theme.palette.text as unknown as Record<string, string>} />
      <ColorSection title="Background" colors={theme.palette.background as unknown as Record<string, string>} />
      <ColorSection title="Action" colors={theme.palette.action as unknown as Record<string, string>} />
      <ColorSection title="Status" colors={theme.palette.statusColors} />
      <ColorSection title="Tag" colors={theme.palette.tag} />
      <ColorSection title="Common" colors={theme.palette.common as unknown as Record<string, string>} />
      <ColorSection title="Grey" colors={theme.palette.grey as unknown as Record<string, string>} />
      <ColorSection title="Divider" colors={{ divider: theme.palette.divider }} />
    </Box>
  );
};

export default ColorsPage;
